import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  Badge,
  DeviceLink,
  EmptyState,
  Input,
  RangePicker,
  Spinner,
} from '@/components';
import type { BehaviorSession, BehaviorSessionsResponse, Range } from '@/lib/admin-types';
import {
  activeDayWindow,
  kindMeta,
  pacificDayKey,
  pacificMinutesOfDay,
  pctInWindow,
  formatPacificDayLabel,
  windowTickLabels,
  type DayWindow,
} from '@/lib/behavior-viz';
import {
  formatAdminDateTime,
  formatAdminTime,
  formatDurationSeconds,
} from '@/lib/format';
import { apiJson } from '@/lib/api';

type SessionsSearch = {
  range: Range;
  installation_id: string;
};

function KindMixBar({ session }: { session: BehaviorSession }) {
  const total = Object.values(session.kind_counts).reduce((a, b) => a + b, 0) || 1;
  const entries = Object.entries(session.kind_counts).sort((a, b) => b[1] - a[1]);
  return (
    <div
      className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
      title={session.summary}
    >
      {entries.map(([kind, count]) => {
        const m = kindMeta(kind);
        return (
          <div
            key={`${session.id}-${kind}`}
            className={clsx('h-full', m.bar)}
            style={{ width: `${(count / total) * 100}%` }}
          />
        );
      })}
    </div>
  );
}

function SessionStrip({
  session,
  dayWindow,
  expanded,
  onToggle,
  motionOn,
}: {
  session: BehaviorSession;
  dayWindow: DayWindow;
  expanded: boolean;
  onToggle: () => void;
  motionOn: boolean;
}) {
  const startMin = pacificMinutesOfDay(session.started_at);
  const endMin = Math.max(
    startMin + Math.max(2, session.duration_seconds / 60),
    pacificMinutesOfDay(session.ended_at)
  );
  const left = Math.max(0, Math.min(100, pctInWindow(startMin, dayWindow)));
  const rawRight = Math.max(left, Math.min(100, pctInWindow(endMin, dayWindow)));
  // Keep short sessions readable without rainbow stacking inside the blob.
  const width = Math.max(2.8, rawRight - left);
  const clampedLeft = Math.min(left, 100 - width);
  const meta = kindMeta(session.primary_kind);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        className="group flex w-full items-start gap-3 text-left"
      >
        <div className="w-36 shrink-0 pt-0.5">
          <DeviceLink
            installationId={session.installation_id}
            nickname={session.nickname}
            className="text-sm font-medium"
          />
          <p className="mt-0.5 text-[11px] tabular-nums text-gray-500">
            {formatAdminTime(session.started_at)} · {formatDurationSeconds(session.duration_seconds)}
          </p>
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="relative h-7 overflow-hidden rounded-full bg-gray-100/90 ring-1 ring-inset ring-gray-200/80">
            <motion.div
              layout
              initial={motionOn ? { opacity: 0, scaleX: 0.85 } : false}
              animate={{ opacity: 1, scaleX: 1 }}
              className={clsx(
                'absolute top-1 bottom-1 min-w-[18px] rounded-full shadow-sm ring-1 ring-black/5',
                meta.bar
              )}
              style={{
                left: `${clampedLeft}%`,
                width: `${width}%`,
                transformOrigin: 'left center',
              }}
              title={session.summary}
            />
          </div>
          <KindMixBar session={session} />
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-gray-600">{session.summary}</span>
            <Badge variant="neutral">{session.event_count} events</Badge>
          </div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && session.events.length > 0 && (
          <motion.ul
            initial={motionOn ? { height: 0, opacity: 0 } : false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={motionOn ? { height: 0, opacity: 0 } : undefined}
            className="ml-36 space-y-1 overflow-hidden border-l-2 border-gray-100 pl-4"
          >
            {session.events.map((e, i) => {
              const m = kindMeta(e.kind);
              return (
                <li
                  key={`${session.id}-ev-${i}`}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm"
                >
                  <span className={clsx('rounded-md px-1.5 py-0.5 text-[11px] font-medium', m.bg, m.color)}>
                    {m.short}
                  </span>
                  <span className="text-gray-800">{e.summary}</span>
                  <span className="text-[11px] tabular-nums text-gray-400">
                    {formatAdminTime(e.at)}
                  </span>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function DaySessions({
  day,
  sessions,
  expanded,
  setExpanded,
  motionOn,
}: {
  day: string;
  sessions: BehaviorSession[];
  expanded: string | null;
  setExpanded: (id: string | null) => void;
  motionOn: boolean;
}) {
  const dayWindow = useMemo(() => {
    const mins: number[] = [];
    for (const s of sessions) {
      mins.push(pacificMinutesOfDay(s.started_at));
      mins.push(
        Math.max(
          pacificMinutesOfDay(s.started_at) + 1,
          pacificMinutesOfDay(s.ended_at)
        )
      );
    }
    return activeDayWindow(mins, { padMin: 40, minSpanMin: 150 });
  }, [sessions]);

  const ticks = windowTickLabels(dayWindow, 5);

  // Nudge overlapping strips into vertical lanes within the day card.
  const stacked = useMemo(() => {
    const sorted = [...sessions].sort(
      (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
    );
    const placed: { end: number; lane: number }[] = [];
    return sorted.map((s) => {
      const start = pacificMinutesOfDay(s.started_at);
      const end = Math.max(
        start + Math.max(2, s.duration_seconds / 60),
        pacificMinutesOfDay(s.ended_at)
      );
      // Visual overlap uses the same min-width inflation roughly (~3% of window).
      const visualPad = (dayWindow.endMin - dayWindow.startMin) * 0.03;
      let lane = 0;
      for (;;) {
        const conflict = placed.some(
          (p) => p.lane === lane && start < p.end + visualPad
        );
        if (!conflict) break;
        lane += 1;
        if (lane > 12) break;
      }
      placed.push({ end, lane });
      return { session: s, lane };
    });
  }, [sessions, dayWindow]);

  const laneCount = Math.max(1, ...stacked.map((s) => s.lane + 1));

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{formatPacificDayLabel(day)}</h3>
        <p className="text-xs text-gray-500">
          {sessions.length} session{sessions.length === 1 ? '' : 's'} · zoomed to active hours
        </p>
      </div>
      <div className="mb-1 flex justify-between pl-36 text-[10px] tabular-nums text-gray-400">
        {ticks.map((t) => (
          <span key={`${day}-${t.min}`}>{t.label}</span>
        ))}
      </div>
      <div className="space-y-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        {/* Compact swimlane overview when many sessions share the same hours */}
        {laneCount > 1 && (
          <div
            className="relative mb-2 rounded-xl bg-gray-50/80 ring-1 ring-inset ring-gray-100"
            style={{ height: Math.min(8, laneCount) * 10 + 16 }}
          >
            {stacked.map(({ session, lane }) => {
              const startMin = pacificMinutesOfDay(session.started_at);
              const endMin = Math.max(
                startMin + Math.max(2, session.duration_seconds / 60),
                pacificMinutesOfDay(session.ended_at)
              );
              const left = Math.max(0, Math.min(100, pctInWindow(startMin, dayWindow)));
              const rawRight = Math.max(left, Math.min(100, pctInWindow(endMin, dayWindow)));
              const width = Math.max(2.8, rawRight - left);
              const meta = kindMeta(session.primary_kind);
              const visibleLane = lane % 8;
              return (
                <div
                  key={`lane-${session.id}`}
                  className={clsx('absolute h-2 min-w-[14px] rounded-full', meta.bar)}
                  style={{
                    left: `${Math.min(left, 100 - width)}%`,
                    width: `${width}%`,
                    top: 8 + visibleLane * 10,
                  }}
                  title={`${session.nickname ?? session.installation_id.slice(0, 8)} · ${session.summary}`}
                />
              );
            })}
          </div>
        )}

        {sessions.map((s) => (
          <SessionStrip
            key={s.id}
            session={s}
            dayWindow={dayWindow}
            expanded={expanded === s.id}
            onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
            motionOn={motionOn}
          />
        ))}
      </div>
      <p className="text-[11px] text-gray-400">
        Updated {formatAdminDateTime(new Date().toISOString())}
      </p>
    </section>
  );
}

function SessionsPage() {
  const { range, installation_id } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/analytics/sessions' });
  const [draftId, setDraftId] = useState(installation_id);
  const [expanded, setExpanded] = useState<string | null>(null);
  const reduceMotion = useReducedMotion();
  const motionOn = !reduceMotion;

  const q = useQuery({
    queryKey: ['admin', 'behavior', 'sessions', range, installation_id],
    queryFn: () => {
      const p = new URLSearchParams({ range, gap_minutes: '30', limit: '120' });
      if (installation_id.trim()) p.set('installation_id', installation_id.trim());
      return apiJson<BehaviorSessionsResponse>(
        `/v1/admin/analytics/behavior/sessions?${p.toString()}`
      );
    },
    refetchInterval: 30_000,
  });

  const byDay = useMemo(() => {
    const map = new Map<string, BehaviorSession[]>();
    for (const s of q.data?.items ?? []) {
      const key = pacificDayKey(s.started_at);
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [q.data?.items]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Session strips</h2>
          <p className="mt-0.5 text-sm text-gray-600">
            Events clustered into sessions (30‑minute gap). Click a strip to expand.
          </p>
        </div>
        <RangePicker
          value={range === '24h' ? '7d' : range}
          options={['7d', '30d', '90d']}
          onChange={(r) =>
            void navigate({ search: (prev) => ({ ...prev, range: r }) })
          }
        />
      </div>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void navigate({
            search: (prev) => ({ ...prev, installation_id: draftId.trim() }),
          });
        }}
      >
        <div className="min-w-[240px] flex-1">
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Filter by installation id (optional)
          </label>
          <Input
            value={draftId}
            onChange={(e) => setDraftId(e.target.value)}
            placeholder="Paste installation UUID"
          />
        </div>
        <button
          type="submit"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white"
        >
          Apply
        </button>
        {installation_id && (
          <button
            type="button"
            className="rounded-xl px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            onClick={() => {
              setDraftId('');
              void navigate({ search: (prev) => ({ ...prev, installation_id: '' }) });
            }}
          >
            Clear
          </button>
        )}
      </form>

      {q.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : q.isError ? (
        <p className="text-sm text-red-600">
          {(q.error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void q.refetch()}>
            Retry
          </button>
        </p>
      ) : byDay.length === 0 ? (
        <EmptyState title="No sessions in this range" />
      ) : (
        <div className="space-y-8">
          {byDay.map(([day, sessions]) => (
            <DaySessions
              key={day}
              day={day}
              sessions={sessions}
              expanded={expanded}
              setExpanded={setExpanded}
              motionOn={motionOn}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/analytics/sessions')({
  validateSearch: (raw: Record<string, unknown>): SessionsSearch => {
    const r = raw.range;
    const range: Range =
      r === '30d' || r === '90d' || r === '7d' || r === '24h' ? (r === '24h' ? '7d' : r) : '7d';
    return {
      range,
      installation_id: typeof raw.installation_id === 'string' ? raw.installation_id : '',
    };
  },
  component: SessionsPage,
});
